import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  CURRENT_REGISTER_CLASS_QUERY_KEY,
  REGISTER_CLASS_LIST_QUERY_KEY,
  REGISTER_LIST_QUERY_KEY,
} from '@/modules/investor-dashboard/queries/registers.tsx';
import { CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { ISSUED_CAPITAL_KEY } from '@/modules/issuer-dashboard/queries/charts.ts';
import {
  ISSUER_NAV_LIST_QUERY_KEY,
  ISSUER_REGISTER_NAV_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/nav.tsx';
import {
  SHAREHOLDERS_HOLDINGS_QUERY_KEY,
  SHAREHOLDER_BALANCE_QUERY_KEY,
  SHAREHOLDER_BALANCE_STATISTIC_QUERY_KEY,
  TOP_HOLDERS_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import {
  CreateRegisterClassRequest,
  GetIssuerRegisterClassListResponse,
  GetIssuersListByRegisterV1SrIssuersIssuersByRegisterIdGetParams,
  IssuerRegisterClassListV1SrRegisterClassesIssuerGetParams,
  UpdateRegisterClassRequest,
} from 'api';

export const ISSUER_REGISTER_CLASS_LIST_QUERY_KEY = 'vestra/issuer-register-class-list';
export const ISSUER_REGISTER_ISSUERS_LIST_QUERY_KEY = 'vestra/issuer-register-issuers-list';

export const useIssuerRegisterClassListQuery = (
  payload: IssuerRegisterClassListV1SrRegisterClassesIssuerGetParams,
  register_id: number,
  disabled = false,
) => {
  return useQuery<GetIssuerRegisterClassListResponse>({
    queryKey: [ISSUER_REGISTER_CLASS_LIST_QUERY_KEY, payload?.search, register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.issuerRegisterClassListV1SrRegisterClassesIssuerGet({
        ...payload,
      });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useGetIssuersByRegisterQuery = (
  payload: GetIssuersListByRegisterV1SrIssuersIssuersByRegisterIdGetParams,
  disabled?: boolean,
) => {
  return useInfiniteQuery({
    queryKey: [ISSUER_REGISTER_ISSUERS_LIST_QUERY_KEY, payload.id, JSON.stringify(payload)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getIssuersListByRegisterV1SrIssuersIssuersByRegisterIdGet(
        { ...payload, page: pageParam || payload.page },
        { headers: { 'xRegister-ID': payload.id } },
      );
      return response.data;
    },
    enabled: !disabled,
    keepPreviousData: true,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;

      if (hasMore) {
        return page + 1;
      }
    },
  });
};

export const useIssuerAddRegisterClassMutation = ({
  setErrors,
  onSuccess,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (payload: CreateRegisterClassRequest) => {
      const response = await withErrorHandling(
        api.v1.createRegisterClassV1SrRegisterClassesPost(payload),
        {
          loading: 'Creating security class...',
          success: 'Security class created',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([TOP_HOLDERS_QUERY_KEY]);
      await queryClient.invalidateQueries({ queryKey: [CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY] });
      await queryClient.invalidateQueries([SHAREHOLDER_BALANCE_STATISTIC_QUERY_KEY]);
      await queryClient.invalidateQueries([SHAREHOLDER_BALANCE_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUED_CAPITAL_KEY]);
      await queryClient.invalidateQueries([REGISTER_CLASS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([REGISTER_LIST_QUERY_KEY]);
      onSuccess && onSuccess();
    },
  });
};

export const useIssuerUpdateRegisterClassMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: UpdateRegisterClassRequest) => {
      const response = await withErrorHandling(
        api.v1.updateRegisterClassV1SrRegisterClassesPatch(payload),
        {
          loading: 'Updating security class...',
          success: 'Security class updated',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [CURRENT_REGISTER_CLASS_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [REGISTER_CLASS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_REGISTER_CLASS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries([TOP_HOLDERS_QUERY_KEY]);
      queryClient.invalidateQueries({ queryKey: [CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY] });
      queryClient.invalidateQueries([SHAREHOLDER_BALANCE_STATISTIC_QUERY_KEY]);
      queryClient.invalidateQueries([SHAREHOLDER_BALANCE_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUED_CAPITAL_KEY]);
      queryClient.invalidateQueries([ISSUER_NAV_LIST_QUERY_KEY]);
    },
  });
};

export const useIssuerDeleteRegisterClassMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.deleteRegisterClassV1SrRegisterClassesIdDelete({ id }),
        {
          loading: 'Deleting security class...',
          success: 'Security class deleted',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [REGISTER_CLASS_LIST_QUERY_KEY],
      });
      await queryClient.invalidateQueries({
        queryKey: [ISSUER_REGISTER_CLASS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries([TOP_HOLDERS_QUERY_KEY]);
      queryClient.invalidateQueries({ queryKey: [CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY] });
      queryClient.invalidateQueries([SHAREHOLDER_BALANCE_STATISTIC_QUERY_KEY]);
      queryClient.invalidateQueries([SHAREHOLDER_BALANCE_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUED_CAPITAL_KEY]);
      queryClient.invalidateQueries([ISSUER_REGISTER_NAV_QUERY_KEY]);
      queryClient.invalidateQueries([SHAREHOLDERS_HOLDINGS_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUER_NAV_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([ISSUER_REGISTER_ISSUERS_LIST_QUERY_KEY]);
    },
  });
};
